import {
  Alert,
  Box,
  Button,
  Container,
  Skeleton,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import NewsletterSignup from '../components/Newsletter/NewsletterSignup';
import ProductCard from '../components/ProductCard';
import { fetchFeaturedProducts } from '../services/productService';

function Home() {
  const navigate = useNavigate();

  // Featured products come from the catalogue (products.is_featured) and link
  // to their own PDPs. They used to be a hardcoded array of Etsy links, which
  // sent every home-page visitor off-site before they ever saw the cart.
  const {
    data: featuredProducts = [],
    isLoading: featuredLoading,
    error: featuredError,
  } = useQuery({
    queryKey: ['featured-products'],
    queryFn: () => fetchFeaturedProducts(3),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          pt: 8,
          pb: 6,
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("/hero-background.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          color: 'white',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontWeight: 'bold',
              mb: 4,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}
            data-testid={"home-initial-header"}
          >
            Handcrafted Leather Goods
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 4 }}
          >
            Quality craftsmanship meets modern design
          </Typography>
          {/* View Collection leads — better cold CTA for new visitors than the configurator */}
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/products')}
            sx={{ mr: 2 }}
          >
            View Collection
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/configurator')}
            sx={{
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: 'background.paper',
              }
            }}
          >
            Wallet Designer
          </Button>
        </Container>
      </Box>

      {/* Featured Products Section */}
      <Container sx={{ py: 8 }} maxWidth="md">
        <Typography
          variant="h3"
          sx={{
            mb: 6,
            textAlign: 'center',
            fontWeight: 'medium',
            color: 'text.primary'
          }}
        >
          Our Most Popular Designs
        </Typography>
        {featuredError && (
          <Alert severity="info" sx={{ mb: 3 }}>
            We couldn&apos;t load our featured pieces just now —{' '}
            <Button size="small" onClick={() => navigate('/products')}>
              browse the full collection
            </Button>
          </Alert>
        )}
        <Grid
          container
          spacing={4}
          direction={{ xs: 'column', md: 'row' }}
          alignItems="stretch"
        >
          {featuredLoading
            ? [0, 1, 2].map((i) => (
                <Grid size={{ xs: 12, md: 4 }} key={i}>
                  <Skeleton variant="rectangular" height={350} sx={{ borderRadius: 1 }} />
                  <Skeleton height={32} sx={{ mt: 1 }} />
                  <Skeleton height={24} width="40%" />
                </Grid>
              ))
            : featuredProducts.map((product) => (
                <Grid size={{ xs: 12, md: 4 }} key={product.id}>
                  <ProductCard
                    product={product}
                    sx={{
                      maxWidth: { xs: '100%', sm: '500px', md: '100%' },
                      mx: 'auto',
                      borderRadius: 2,
                      '&:hover': { transform: 'translateY(-4px)' },
                      transition: 'all 0.3s ease-in-out',
                    }}
                  />
                </Grid>
              ))}
        </Grid>
        {!featuredLoading && featuredProducts.length > 0 && (
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Button variant="outlined" size="large" onClick={() => navigate('/products')}>
              Shop All Products
            </Button>
          </Box>
        )}
      </Container>

      {/* Configurator Promo Section */}
      <Container maxWidth="md">
        <Typography variant="h3" gutterBottom>
          Design Your Perfect Wallet
        </Typography>
        <Grid container alignItems="center" spacing={4} justifyContent={{ xs: 'center', md: 'space-between' }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
              Interactive Custom Wallet Builder
            </Typography>
            <Box component="ul" sx={{ mb: 4 }}>
              {[
                'Choose from our signature wallet designs',
                'Select premium Italian leather colors',
                'Customize individual pockets and sections',
                'Create unique two-tone combinations',
                'Preview your design in real-time'
              ].map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{
                    typography: 'body1',
                    mb: 1.5,
                    '&::marker': {
                      color: 'primary.main'
                    }
                  }}
                >
                  {point}
                </Box>
              ))}
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/configurator')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                Start Designing
              </Button>
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src="/configurator-preview.png"
              alt="Wallet Configurator Preview"
              sx={{
                border: '20px solid #ccc',
                width: '350px',
                height: 'auto',
                display: 'block',
                borderRadius: 1
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Newsletter Section */}
      <Container sx={{ py: 8 }} maxWidth="lg">
        <NewsletterSignup />
      </Container>
    </Box>
  );
}


export default Home;
